import { useCallback, useEffect, useMemo, useState } from "react";
import api from "../utils/api";

function normalizeChannelIds(channelIds) {
  if (!Array.isArray(channelIds)) return [];
  return channelIds.map((c) => String(c).trim()).filter(Boolean);
}

export function useYoutubeTracker(channelIds, startDate, endDate, refreshKey = 0) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const ids = useMemo(() => normalizeChannelIds(channelIds), [channelIds]);
  const start = (startDate || "").trim();
  const end = (endDate || "").trim();

  const load = useCallback(
    async (controller) => {
      if (!ids.length || !start) {
        setData([]);
        return;
      }

      setLoading(true);
      setError(null);

      const channelParam = ids.map((id) => encodeURIComponent(id)).join(",");
      const url = `/api/get-youtube-tracker?c=${channelParam}&start=${encodeURIComponent(
        start
      )}&end=${encodeURIComponent(end || start)}`;

      // This query is occasionally just slow rather than actually broken —
      // one retry after a beat clears most of what would otherwise show up
      // as a hard failure for a page the person only has to reload anyway.
      for (let attempt = 0; attempt < 2; attempt++) {
        try {
          const res = await api.get(url, { signal: controller.signal });
          setData(Array.isArray(res.data) ? res.data : []);
          setLoading(false);
          return;
        } catch (err) {
          if (err?.name === "CanceledError") return;
          if (attempt === 0) {
            await new Promise((r) => setTimeout(r, 1500));
            continue;
          }
          console.error("youtube tracker fetch error", err);
          setError("Failed to load YouTube tracker data.");
          setData([]);
        }
      }

      setLoading(false);
    },
    [ids, start, end]
  );

  useEffect(() => {
    const ac = new AbortController();
    load(ac);
    return () => ac.abort();
  }, [load, refreshKey]);

  return { data, loading, error };
}